import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import axios from 'axios';
import { useState } from 'react';
 
// Accept the onUserAdded callback prop
export default function AddUserForm({ open, onClose, onUserAdded }) {
    // State variables for the user form
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [emailAddress, setEmailAddress] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('');
    const [userType, setUserType] = useState('normal');
    const [company, setCompany] = useState('');
    const [status, setStatus] = useState(''); // Default status
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false); // Track if form is submitted
 
    // Handle form submission
    const handleSubmit = async () => {
        setError('');
        setSubmitted(true); // Mark the form as submitted
 
        // Validate required fields (Simplified validation, status removed)
        if (!firstName || !lastName || !emailAddress || !password || !role || !userType || !company) {
            setError('All fields are required.');
            return;
        }
 
        // Validate email format
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
        if (!emailRegex.test(emailAddress)) {
            setError('Invalid email address format.');
            return;
        }
 
        setLoading(true);
        try {
            const newUser = {
                firstName,
                lastName,
                emailAddress,
                password,
                role,
                userType, // 'normal' or 'npa'
                company, // Send empty string if left blank
                status, // Send 'pending'
            };
 
            console.log('Submitting user:', newUser); // Debugging log
 
            // Send POST request
            const response = await axios.post('/api/user', newUser);
 
            if (response.status === 201) {
                // 🎯 CRITICAL FIX: Call the callback function to notify the parent to refresh
                if (onUserAdded) {
                    onUserAdded(response.data.user);
                }
                
                // Clear the form fields after successful submission
                setFirstName(''); setLastName(''); setEmailAddress(''); setPassword(''); setRole(''); setUserType('normal'); setCompany('');
                setStatus(''); 
                setError('');
                setSubmitted(false);
                onClose(); // Close the dialog
            } else {
                // This path should ideally not be hit with a 2xx status, but included for completeness
                setError('Failed to add user.');
            }
        } catch (error) {
            console.error('Error adding user:', error);
            if (error.response) {
                // Handle 409 (Conflict/Email exists) or other server errors
                const message = error.response.data.message || 'Server error occurred.';
                setError(message);
            } else if (error.request) {
                setError('Network error: Failed to communicate with the server.');
            } else {
                setError('An unexpected error occurred.');
            }
        } finally {
            setLoading(false);
        }
    };
 
    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Add New User</DialogTitle>
            <DialogContent>
                {/* Display general error message */}
                {error && <FormHelperText error sx={{ mb: 1 }}>{error}</FormHelperText>}
                
                {/* First Name */}
                <TextField
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    fullWidth
                    margin="normal"
                    error={submitted && !firstName}
                    helperText={submitted && !firstName ? 'This field is required' : ''}
                />
 
                {/* Last Name */}
                <TextField
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    fullWidth
                    margin="normal"
                    error={submitted && !lastName}
                    helperText={submitted && !lastName ? 'This field is required' : ''}
                />
 
                {/* Email Address */}
                <TextField
                    label="Email Address"
                    type="email"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    fullWidth
                    margin="normal"
                    error={submitted && !emailAddress}
                    helperText={submitted && !emailAddress ? 'This field is required' : ''}
                />
 
                {/* Password */}
                <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    margin="normal"
                    error={submitted && !password}
                    helperText={submitted && !password ? 'This field is required' : ''}
                />
 
                {/* Company */}
                <TextField
                    label="Company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    fullWidth
                    margin="normal"
                    error={submitted && !company}
                    helperText={submitted && !company ? 'This field is required' : ''}
                />
 
                {/* Role Dropdown */}
                <FormControl fullWidth margin="normal" error={submitted && !role}>
                    <InputLabel>Role</InputLabel>
                    <Select value={role} onChange={(e) => setRole(e.target.value)} label="Role">
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="user">User</MenuItem>
                        <MenuItem value="guest">Guest</MenuItem>
                    </Select>
                    {submitted && !role && <FormHelperText>{'This field is required'}</FormHelperText>}
                </FormControl>

                {/* User Type Dropdown - decides whether the user sees NPA or normal (non-NPA) features */}
                <FormControl fullWidth margin="normal" error={submitted && !userType}>
                    <InputLabel>User Type</InputLabel>
                    <Select value={userType} onChange={(e) => setUserType(e.target.value)} label="User Type">
                        <MenuItem value="normal">Normal</MenuItem>
                        <MenuItem value="npa">NPA</MenuItem>
                    </Select>
                    {submitted && !userType && <FormHelperText>{'This field is required'}</FormHelperText>}
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}